import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {ScrollRestoration | null} */
  #previousScrollRestoration = null;

  /**
   * While the drawer drives the history stack (mobile), stop the browser from
   * auto-restoring scroll on `history.back()`. The pushed entry records scroll
   * as 0 (the body is scroll-locked when the drawer opens), so native
   * restoration would jump the page to the top and override the manual restore
   * done in DialogComponent.closeDialog. We re-enable it after the drawer closes.
   */
  #lockScrollRestoration() {
    if (!('scrollRestoration' in history)) return;
    if (this.#previousScrollRestoration == null) {
      this.#previousScrollRestoration = history.scrollRestoration;
    }
    history.scrollRestoration = 'manual';
  }

  #restoreScrollRestoration() {
    if (!('scrollRestoration' in history)) return;
    if (this.#previousScrollRestoration == null) return;
    history.scrollRestoration = this.#previousScrollRestoration;
    this.#previousScrollRestoration = null;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#restoreScrollRestoration();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    this.#lockScrollRestoration();

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
    // Re-enable native scroll restoration after the back navigation settles, so
    // normal browsing keeps its scroll memory. The manual scroll restore in
    // closeDialog has already run by this point.
    setTimeout(() => this.#restoreScrollRestoration(), 400);
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
    setTimeout(() => this.#restoreScrollRestoration(), 400);
  };

  /** @param {Event & { detail?: { data?: { fromQuickAdd?: boolean } } }} event */
  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;

    // El "fast add to cart" no debe abrir el sidecart, solo actualizarlo. El
    // contenido del carrito (cart-items-component) y el contador del icono
    // (cart-icon) se actualizan igualmente porque escuchan el mismo evento
    // cart:update de forma independiente a showDialog().
    //
    // Cubre los dos flujos del quick add:
    //  1. "Elegir opciones" → modal → añadir: QuickAddDialog marca el evento
    //     (detail.data.fromQuickAdd) durante el bubbling, antes de llegar aquí.
    //  2. Botón "Añadir" directo de la tarjeta: el evento procede de un
    //     product-form-component que vive dentro de <quick-add-component>.
    if (event.detail?.data?.fromQuickAdd) return;

    const target = event.target;
    if (target instanceof Element && target.closest('quick-add-component, quick-add-dialog')) return;

    this.showDialog();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

// ── COUNTDOWN DE RESERVA ──────────────────────────────────────
let cartReservationTimer;
function initCartReservation() {
  clearInterval(cartReservationTimer);
  const el = document.getElementById('cart-reservation-countdown');
  if (!el) return;

  const STORAGE_KEY = 'cart-reservation-end';
  const DURATION = 10 * 60 * 1000; // 10 minutos

  let endTime = parseInt(sessionStorage.getItem(STORAGE_KEY) || '0');
  if (!endTime || endTime < Date.now()) {
    endTime = Date.now() + DURATION;
    sessionStorage.setItem(STORAGE_KEY, String(endTime));
  }

  function tick() {
    const el = document.getElementById('cart-reservation-countdown');
    if (!el) return;
    const remaining = endTime - Date.now();
    if (remaining <= 0) {
      el.textContent = '0:00';
      // Renovar el timer cuando llega a 0
      endTime = Date.now() + DURATION;
      sessionStorage.setItem(STORAGE_KEY, String(endTime));
      return;
    }
    const mins = Math.floor(remaining / 60000);
    const secs = Math.floor((remaining % 60000) / 1000);
    el.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  tick();
  cartReservationTimer = setInterval(tick, 1000);
}

// ── UPSELLS ───────────────────────────────────────────────────
let upsellsPending = false;
const upsellsRendered = new WeakMap();
const upsellsCache = new Map();
const escapeUpsell = (value) => String(value ?? '').replace(/[&<>"']/g, (char) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
async function initCartUpsells(force = false) {
  const list = document.getElementById('cart-upsells-list');
  const wrap = document.getElementById('cart-upsells');
  if (!list || !wrap || upsellsPending || !wrap.closest('dialog[open]')) return;
  if (!force && upsellsRendered.get(list) === list.innerHTML) return;
  upsellsPending = true;

  try {
    const cartRes  = await fetch(Theme.routes.cart_json_url);
    if (!cartRes.ok) throw new Error('Cart recommendations unavailable');
    const cart = await cartRes.json();
    if (!cart.items?.length) { wrap.style.display = 'none'; return; }

    const productId = cart.items[0].product_id;
    let products = upsellsCache.get(productId);
    if (!products) {
      const recRes = await fetch(`${Theme.routes.root_url || '/'}recommendations/products.json?product_id=${productId}&limit=2&intent=related`);
      if (!recRes.ok) throw new Error('Product recommendations unavailable');
      products = (await recRes.json()).products || [];
      upsellsCache.set(productId, products);
    }
    if (!list.isConnected) return;

    if (!products?.length) { wrap.style.display = 'none'; return; }

    const cartProductIds = cart.items.map(i => i.product_id);
    const filtered = products.filter(p => p.available && !cartProductIds.includes(p.id));

    if (!filtered.length) { wrap.style.display = 'none'; return; }

    wrap.style.display = '';
    list.innerHTML = filtered.map(p => {
      const variants = p.variants;
      const image = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=160` : '';
      const price    = (p.price / 100).toLocaleString(document.documentElement.lang === 'es' ? 'es-AR' : document.documentElement.lang || 'es-AR', { style: 'currency', currency: cart.currency || 'ARS' });

      return `
        <div class="cart-upsell-item" data-product-id="${p.id}">
          <img class="cart-upsell-item__image" src="${escapeUpsell(image)}" alt="${escapeUpsell(p.title)}" loading="lazy" width="80" height="100">
          <div class="cart-upsell-item__info">
            <p class="cart-upsell-item__title">${escapeUpsell(p.title)}</p>
            <p class="cart-upsell-item__price">${price}</p>
            <div class="cart-upsell-item__select--content">
              <select class="cart-upsell-item__select" id="upsell-select-${p.id}" aria-label="${escapeUpsell(p.title)}">
                ${variants.map(v => `<option value="${v.id}" ${!v.available ? 'disabled' : ''}>${escapeUpsell(v.title)}</option>`).join('')}
              </select>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <mask id="path-1-inside-1_9748_9864" fill="white">
                  <path d="M9.94857 4.32034L6.19857 8.07034C6.14584 8.123 6.07435 8.15259 5.99982 8.15259C5.92529 8.15259 5.85381 8.123 5.80107 8.07034L2.05107 4.32034C2.00139 4.26702 1.97434 4.1965 1.97563 4.12364C1.97692 4.05078 2.00643 3.98126 2.05796 3.92973C2.10949 3.8782 2.17901 3.84868 2.25187 3.84739C2.32474 3.84611 2.39525 3.87316 2.44857 3.92284L5.99982 7.47362L9.55107 3.92284C9.60439 3.87316 9.6749 3.84611 9.74777 3.84739C9.82063 3.84868 9.89015 3.8782 9.94168 3.92973C9.99321 3.98126 10.0227 4.05078 10.024 4.12364C10.0253 4.1965 9.99825 4.26702 9.94857 4.32034Z"/>
                </mask>
                <path d="M9.94857 4.32034L6.19857 8.07034C6.14584 8.123 6.07435 8.15259 5.99982 8.15259C5.92529 8.15259 5.85381 8.123 5.80107 8.07034L2.05107 4.32034C2.00139 4.26702 1.97434 4.1965 1.97563 4.12364C1.97692 4.05078 2.00643 3.98126 2.05796 3.92973C2.10949 3.8782 2.17901 3.84868 2.25187 3.84739C2.32474 3.84611 2.39525 3.87316 2.44857 3.92284L5.99982 7.47362L9.55107 3.92284C9.60439 3.87316 9.6749 3.84611 9.74777 3.84739C9.82063 3.84868 9.89015 3.8782 9.94168 3.92973C9.99321 3.98126 10.0227 4.05078 10.024 4.12364C10.0253 4.1965 9.99825 4.26702 9.94857 4.32034Z" fill="#020618"/>
                <path d="M9.94857 4.32034L10.6557 5.02744L10.6682 5.01497L10.6802 5.00206L9.94857 4.32034ZM6.19857 8.07034L6.90524 8.77788L6.90568 8.77744L6.19857 8.07034ZM5.80107 8.07034L5.09396 8.77744L5.0944 8.77788L5.80107 8.07034ZM2.05107 4.32034L1.31946 5.00206L1.33149 5.01497L1.34396 5.02744L2.05107 4.32034ZM2.44857 3.92284L3.15563 3.21568L3.14318 3.20323L3.13029 3.19122L2.44857 3.92284ZM5.99982 7.47362L5.29276 8.18077L5.99982 8.88774L6.70688 8.18077L5.99982 7.47362ZM9.55107 3.92284L8.86935 3.19122L8.85646 3.20323L8.84401 3.21568L9.55107 3.92284ZM9.94857 4.32034L9.24146 3.61323L5.49146 7.36323L6.19857 8.07034L6.90568 8.77744L10.6557 5.02744L9.94857 4.32034ZM6.19857 8.07034L5.4919 7.36279C5.62667 7.22819 5.80935 7.15259 5.99982 7.15259V8.15259V9.15259C6.33935 9.15259 6.665 9.01782 6.90524 8.77788L6.19857 8.07034ZM5.99982 8.15259V7.15259C6.19029 7.15259 6.37297 7.22819 6.50774 7.36279L5.80107 8.07034L5.0944 8.77788C5.33464 9.01782 5.66029 9.15259 5.99982 9.15259V8.15259ZM5.80107 8.07034L6.50818 7.36323L2.75818 3.61323L2.05107 4.32034L1.34396 5.02744L5.09396 8.77744L5.80107 8.07034ZM2.05107 4.32034L2.78268 3.63861C2.90964 3.77486 2.97876 3.95508 2.97547 4.14128L1.97563 4.12364L0.975785 4.106C0.969929 4.43793 1.09314 4.75918 1.31946 5.00206L2.05107 4.32034ZM1.97563 4.12364L2.97547 4.14128C2.97219 4.32749 2.89676 4.50515 2.76507 4.63683L2.05796 3.92973L1.35086 3.22262C1.11611 3.45737 0.981642 3.77407 0.975785 4.106L1.97563 4.12364ZM2.05796 3.92973L2.76507 4.63683C2.63338 4.76852 2.45572 4.84395 2.26952 4.84724L2.25187 3.84739L2.23423 2.84755C1.9023 2.85341 1.5856 2.98788 1.35086 3.22262L2.05796 3.92973ZM2.25187 3.84739L2.26952 4.84724C2.08331 4.85052 1.9031 4.78141 1.76685 4.65445L2.44857 3.92284L3.13029 3.19122C2.88741 2.96491 2.56617 2.84169 2.23423 2.84755L2.25187 3.84739ZM2.44857 3.92284L1.74151 4.62999L5.29276 8.18077L5.99982 7.47362L6.70688 6.76646L3.15563 3.21568L2.44857 3.92284ZM5.99982 7.47362L6.70688 8.18077L10.2581 4.62999L9.55107 3.92284L8.84401 3.21568L5.29276 6.76646L5.99982 7.47362ZM9.55107 3.92284L10.2328 4.65445C10.0965 4.78141 9.91633 4.85052 9.73013 4.84724L9.74777 3.84739L9.76541 2.84755C9.43347 2.84169 9.11223 2.96491 8.86935 3.19122L9.55107 3.92284ZM9.74777 3.84739L9.73013 4.84724C9.54392 4.84395 9.36626 4.76852 9.23457 4.63683L9.94168 3.92973L10.6488 3.22262C10.414 2.98788 10.0973 2.85341 9.76541 2.84755L9.74777 3.84739ZM9.94168 3.92973L9.23457 4.63683C9.10288 4.50515 9.02745 4.32749 9.02417 4.14128L10.024 4.12364L11.0239 4.106C11.018 3.77407 10.8835 3.45737 10.6488 3.22262L9.94168 3.92973ZM10.024 4.12364L9.02417 4.14128C9.02088 3.95508 9.09 3.77487 9.21696 3.63861L9.94857 4.32034L10.6802 5.00206C10.9065 4.75917 11.0297 4.43793 11.0239 4.106L10.024 4.12364Z" fill="#020618" mask="url(#path-1-inside-1_9748_9864)"/>
              </svg>
            </div>
          </div>
          <button
            class="cart-upsell-item__btn"
            onclick="addUpsellToCart(${p.id})"
            aria-label="${escapeUpsell(wrap.dataset.addLabel)}"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none">
              <g clip-path="url(#clip0_9749_1613)">
                <path d="M4.66422 3.6023V4.53515H8.3956V3.6023C8.3956 2.58201 7.55021 1.73662 6.52991 1.73662C5.48045 1.73662 4.66422 2.58201 4.66422 3.6023ZM3.73138 5.46799H0.932844V14.7964H12.127V5.46799H9.32844V7.8001V8.26653H8.3956V7.8001V5.46799H4.66422V7.8001V8.26653H3.73138V7.8001V5.46799ZM3.73138 4.53515V3.6023C3.73138 2.05728 4.98489 0.803772 6.52991 0.803772C8.07493 0.803772 9.32844 2.05728 9.32844 3.6023V4.53515H12.127H13.0598V5.46799V14.7964V15.7293H12.127H0.932844H0V14.7964V5.46799V4.53515H0.932844H3.73138Z" fill="white"/>
              </g>
              <defs>
                <clipPath id="clip0_9749_1613">
                  <rect width="13.8594" height="16" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          </button>
        </div>
      `;
    }).join('');

  } catch (e) {
    console.error('Upsells error:', e);
    wrap.style.display = 'none';
  } finally {
    upsellsPending = false;
    if (list.isConnected) upsellsRendered.set(list, list.innerHTML);
    else if (document.querySelector('cart-drawer-component dialog[open] #cart-upsells-list')) initCartUpsells();
  }
}

window.addUpsellToCart = async function(productId) {
  const select    = document.getElementById(`upsell-select-${productId}`);
  const variantId = parseInt(select?.value);
  if (!variantId) return;

  const btn = document.querySelector(`.cart-upsell-item[data-product-id="${productId}"] .cart-upsell-item__btn`);
  if (btn?.disabled) return;
  if (btn) { btn.style.opacity = '0.5'; btn.disabled = true; }

  try {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: sectionIds.join(','), 
      }),
    });

    const data = await response.json();

    if (data.status) {
      console.error('Error adding upsell:', data.message);
      return;
    }

    const cartItemsComponent = document.querySelector('cart-items-component');

    // if (cartItemsComponent) {
    //   cartItemsComponent.dispatchEvent(
    //     new CustomEvent('cart:update', {
    //       bubbles: true,
    //       detail: {
    //         resource: data,
    //         sourceId: 'upsell',
    //         data: {
    //           source: 'upsell',
    //           sections: data.sections,
    //         },
    //       },
    //     })
    //   );
    // }

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: data,
          sourceId: 'upsell',
          data: {
            source: 'upsell',
            sections: data.sections,
          },
        },
      })
    );



  } catch(e) {
    console.error('Upsell add error:', e);
  } finally {
    if (btn) { btn.style.opacity = ''; btn.disabled = false; }
  }
};

// ── INIT cuando el drawer se abre ────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer) return;

  // Cuando el drawer se abre
  drawer.addEventListener('dialog:open', () => {
    initCartReservation();
    initCartUpsells(true);
  });
  drawer.addEventListener('dialog:close', () => clearInterval(cartReservationTimer));

  // Si ya está abierto al cargar
  if (drawer.querySelector('dialog[open]')) {
    initCartReservation();
    initCartUpsells();
  }

  // Respaldo: MutationObserver sobre el contenido del drawer
  // para detectar cuando Horizon reemplaza el DOM
  const dialog = drawer.querySelector('dialog');
  if (dialog) {
    const observer = new MutationObserver(() => {
      const upsellsWrap = document.getElementById('cart-upsells');
      // Si el contenedor existe pero la lista está vacía, re-inicializar
      if (upsellsWrap) {
        const list = document.getElementById('cart-upsells-list');
        if (list && list.children.length === 0) {
          initCartUpsells();
        }
      }
    });

    observer.observe(dialog, { childList: true, subtree: true });
  }
});


